import { useState } from 'react';
import { motion } from 'framer-motion';
import Button from '@components/common/Button';

/**
 * AdminForms Component
 * Props: fields array [{name, type, required, validation}], onSubmit
 * Features:
 * - CRUD forms
 * - Field types: text, number, select, toggle, date
 * - Validation rules: min/max, regex
 * - Animations: fade-in, input focus effects
 */

const AdminForms = ({ fields = [], onSubmit, initialData = {}, submitButtonText = 'Submit' }) => {
  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({});

  const validateField = (field, value) => {
    if (field.required && !value) {
      return `${field.label} is required`;
    }

    if (field.validation) {
      if (field.validation.min !== undefined && value < field.validation.min) {
        return `${field.label} must be at least ${field.validation.min}`;
      }
      if (field.validation.max !== undefined && value > field.validation.max) {
        return `${field.label} must be at most ${field.validation.max}`;
      }
      if (field.validation.regex && !new RegExp(field.validation.regex).test(value)) {
        return field.validation.message || `${field.label} is invalid`;
      }
      if (field.validation.minLength && value.length < field.validation.minLength) {
        return `${field.label} must be at least ${field.validation.minLength} characters`;
      }
    }

    return null;
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field.name]: value });
    
    // Clear error when user starts typing
    if (errors[field.name]) {
      setErrors({ ...errors, [field.name]: null });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate all fields
    const newErrors = {};
    fields.forEach((field) => {
      const error = validateField(field, formData[field.name]);
      if (error) {
        newErrors[field.name] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData);
  };

  const renderField = (field) => {
    const commonProps = {
      id: field.name,
      value: formData[field.name] || '',
      onChange: (e) => handleChange(field, e.target.value),
      className: `input ${errors[field.name] ? 'border-red-500' : ''}`,
    };

    switch (field.type) {
      case 'text':
      case 'email':
      case 'password':
        return (
          <input
            type={field.type}
            placeholder={field.placeholder}
            {...commonProps}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            placeholder={field.placeholder}
            min={field.validation?.min}
            max={field.validation?.max}
            step={field.step || 1}
            {...commonProps}
          />
        );

      case 'textarea':
        return (
          <textarea
            rows={field.rows || 4}
            placeholder={field.placeholder}
            {...commonProps}
          />
        );

      case 'select':
        return (
          <select {...commonProps}>
            <option value="">Select {field.label}</option>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'toggle':
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              id={field.name}
              checked={formData[field.name] || false}
              onChange={(e) => handleChange(field, e.target.checked)}
              className="h-4 w-4 text-secondary-500 focus:ring-secondary-400 border-gray-300 rounded"
            />
            <label htmlFor={field.name} className="ml-2 text-sm text-gray-700">
              {field.toggleLabel || field.label}
            </label>
          </div>
        );

      case 'date':
        return (
          <input
            type="date"
            {...commonProps}
          />
        );

      case 'file':
        return (
          <input
            type="file"
            accept={field.accept}
            onChange={(e) => handleChange(field, e.target.files[0])}
            className={`input ${errors[field.name] ? 'border-red-500' : ''}`}
          />
        );

      default:
        return <input type="text" {...commonProps} />;
    }
  };

  return (
    <motion.form
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      onSubmit={handleSubmit}
      className="space-y-6"
    >
      {fields.map((field, index) => (
        <motion.div
          key={field.name}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.05 }}
        >
          {field.type !== 'toggle' && (
            <label htmlFor={field.name} className="label">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
          )}
          
          {renderField(field)}
          
          {errors[field.name] && (
            <p className="mt-1 text-sm text-red-500">{errors[field.name]}</p>
          )}
          
          {field.helpText && (
            <p className="mt-1 text-sm text-gray-500">{field.helpText}</p>
          )}
        </motion.div>
      ))}

      <div className="flex justify-end space-x-4 pt-4">
        <Button
          text={submitButtonText}
          type="primary"
          onClick={handleSubmit}
        />
      </div>
    </motion.form>
  );
};

export default AdminForms;

